import { laborCostFromHours, rateProfileForGroup } from "./plannerQuoteRates";
import { formatMoney, newEstimateLineId } from "./quotePricingMath";

function childMap(items) {
    const map = new Map();
    for (const item of items || []) {
        const key = item.parentId || "";
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(item);
    }
    return map;
}

function parseTarget(value) {
    const match = String(value ?? "").replace(/,/g, "").match(/(\$)?\s*(\d+(?:\.\d+)?)\s*([kK])?/);
    if (!match) return null;
    const amount = Number(match[2]) * (match[3] ? 1000 : 1);
    return Number.isFinite(amount) ? amount : null;
}

function customerPurchaseAmount(item) {
    if (String(item?.lineKind || "").toLowerCase() !== "purchase") return 0;
    if (String(item?.purchaseTreatment || "").toLowerCase() !== "customer") return 0;
    const direct = Number(item?.quoteCustomerPurchases);
    if (Number.isFinite(direct) && direct > 0) return direct;
    const unitCost = Number.parseFloat(String(item?.unitCost ?? "").replace(/[$,]/g, ""));
    const quantity = Number.parseFloat(String(item?.qty ?? "").replace(/[$,]/g, ""));
    if (!Number.isFinite(unitCost) || unitCost <= 0) return 0;
    return unitCost * (Number.isFinite(quantity) && quantity > 0 ? quantity : 1);
}

function isHiddenFromCustomerFlag(item) {
    return item?.hideFromCustomer === true || item?.hideFromCustomer === 1 || item?.hideFromCustomer === "1";
}

function quoteNode(item, children, materialMarkupPercent = 12, ancestorHidden = false) {
    const hideFromCustomer = isHiddenFromCustomerFlag(item);
    const customerVisible = !ancestorHidden && !hideFromCustomer;
    const ownHours = Number(item.quoteOwnLaborHours) || 0;
    const ownCost = Number(item.quoteOwnCost) || 0;
    const ownPurchases = Number(item.quoteOwnPurchases) || 0;
    const ownCustomerPurchases = customerPurchaseAmount(item);
    const ownPurchasesCustomer = ownPurchases * (1 + materialMarkupPercent / 100);
    const childRows = (children.get(item.id) || [])
        .filter((child) => !child.archived)
        .sort((a, b) => String(a.sortOrder || "").localeCompare(String(b.sortOrder || "")))
        .map((child) => quoteNode(child, children, materialMarkupPercent, !customerVisible));
    const rolled = childRows.reduce(
        (acc, child) => ({
            laborHours: acc.laborHours + child.laborHours,
            laborCost: acc.laborCost + child.laborCost,
            purchases: acc.purchases + child.purchases,
            customerPurchases: acc.customerPurchases + child.customerPurchases,
            customerLaborHours: acc.customerLaborHours + child.customerLaborHours,
            customerLaborCost: acc.customerLaborCost + child.customerLaborCost,
            customerPurchaseCost: acc.customerPurchaseCost + child.customerPurchaseCost,
            customerPurchasesCharge: acc.customerPurchasesCharge + child.customerPurchasesCharge,
            customerSupplied: acc.customerSupplied + child.customerSupplied,
        }),
        {
            laborHours: 0,
            laborCost: 0,
            purchases: 0,
            customerPurchases: 0,
            customerLaborHours: 0,
            customerLaborCost: 0,
            customerPurchaseCost: 0,
            customerPurchasesCharge: 0,
            customerSupplied: 0,
        }
    );
    const laborHours = ownHours + rolled.laborHours;
    const laborCost = ownCost + rolled.laborCost;
    const purchases = ownPurchases + rolled.purchases;
    const customerPurchases = ownCustomerPurchases + rolled.customerPurchases;
    const purchasesCustomer = purchases * (1 + materialMarkupPercent / 100);
    const costBasisTotal = laborCost + purchases;
    const builtUp = laborCost + purchasesCustomer;
    const roughTotal = builtUp + customerPurchases;
    const customerLaborHours = (customerVisible ? ownHours : 0) + rolled.customerLaborHours;
    const customerLaborCost = (customerVisible ? ownCost : 0) + rolled.customerLaborCost;
    const customerPurchaseCost = (customerVisible ? ownPurchases : 0) + rolled.customerPurchaseCost;
    const customerPurchasesCharge = (customerVisible ? ownPurchasesCustomer : 0) + rolled.customerPurchasesCharge;
    const customerSupplied = (customerVisible ? ownCustomerPurchases : 0) + rolled.customerSupplied;
    const customerBuiltUp = customerLaborCost + customerPurchasesCharge;
    const contingencyPct = Number(item.contingencyPct) > 0 ? Number(item.contingencyPct) : 0;
    const riskLoadedTotal = builtUp * (1 + contingencyPct / 100);
    const customerRiskLoadedTotal = customerBuiltUp * (1 + contingencyPct / 100);
    const target = parseTarget(item.budgetTarget);
    return {
        ...item,
        hideFromCustomer,
        customerVisible,
        lineKind: item.lineKind === "labor" || item.lineKind === "purchase"
            ? item.lineKind
            : item.parentId
                ? "component"
                : "job",
        target,
        ownHours,
        ownCost,
        ownPurchases,
        ownPurchasesCustomer,
        purchasesCustomer,
        ownCustomerPurchases,
        laborHours,
        laborCost,
        purchases,
        materialMarkupPercent,
        costBasisTotal,
        customerPurchases,
        builtUp,
        customerLaborHours,
        customerLaborCost,
        customerPurchaseCost,
        customerPurchasesCharge,
        customerSupplied,
        customerBuiltUp,
        customerRiskLoadedTotal,
        roughTotal,
        contingencyPct,
        riskLoadedTotal,
        variance: target == null ? null : target - builtUp,
        children: childRows,
    };
}

export function buildWixComponentTree(job, items, settings = {}, rootItem = null) {
    const root = rootItem || job;
    if (!root?.id) return [];
    const descendants = collectJobItems(root.id, items).filter((item) => item.id !== root.id);
    const children = childMap(descendants);
    if (rootItem) return [quoteNode(root, children, Number(settings.material_markup_percent) || 12)];
    return descendants
        .filter((item) => item.parentId === root.id && item.lineKind !== "labor" && item.lineKind !== "purchase")
        .sort((a, b) => String(a.sortOrder || "").localeCompare(String(b.sortOrder || "")))
        .map((item) => quoteNode(item, children, Number(settings.material_markup_percent) || 12));
}

export function robotCostFromWixComponents(components) {
    return customerPurchaseBreakdownFromWixComponents(components).robot;
}

export function customerPurchaseBreakdownFromWixComponents(components) {
    function walk(nodes) {
        return (nodes || []).reduce((breakdown, node) => {
            if (node.customerVisible === false) return breakdown;
            const isRobotPurchase =
                node.lineKind === "purchase" && /\brobot\b/i.test(String(node.title || ""));
            const ownCustomerValue = Number(node.ownCustomerPurchases) || 0;
            const ownRobotValue = isRobotPurchase ? ownCustomerValue : 0;
            const childBreakdown = walk(node.children);
            return {
                robot: breakdown.robot + ownRobotValue + childBreakdown.robot,
                other: breakdown.other + Math.max(0, ownCustomerValue - ownRobotValue) + childBreakdown.other,
            };
        }, { robot: 0, other: 0 });
    }
    return walk(components);
}

export function collectJobItems(jobId, items) {
    const kids = childMap(items);
    const byId = new Map((items || []).map((row) => [row.id, row]));
    const out = [];
    function walk(id, seen = new Set()) {
        if (!id || seen.has(id)) return;
        seen.add(id);
        const item = byId.get(id);
        if (!item || item.archived) return;
        out.push(item);
        for (const child of kids.get(id) || []) walk(child.id, seen);
    }
    walk(jobId);
    return out;
}

export function buildWixJobQuote(job, items, groups, settings = {}, scopeItem = null) {
    const materialMarkupPercent = Number.isFinite(Number(settings.material_markup_percent))
        ? Number(settings.material_markup_percent)
        : 12;
    const quoteRoot = scopeItem || job;
    const descendants = collectJobItems(quoteRoot?.id, items);
    const groupMeta = new Map((groups || []).map((g) => [g.key, g]));
    const byGroup = new Map();

    for (const item of descendants) {
        const isLabor = item.lineKind === "labor";
        const hours = Number(item.quoteOwnLaborHours) || 0;
        const purchases = Number(item.quoteOwnPurchases) || 0;
        const customerPurchases = customerPurchaseAmount(item);
        const wixCost = Number(item.quoteOwnCost) || 0;
        if (!hours && !purchases && !customerPurchases && !wixCost) continue;

        const fallbackProfile = rateProfileForGroup({ group_key: item.groupKey, label: item.groupKey || item.title });
        const profile = isLabor
            ? {
                id: item.quoteLaborCategory || item.laborCategory || fallbackProfile.id,
                label: item.quoteRateLabel || fallbackProfile.label,
                hourlyRate: Number(item.quoteHourlyRate) || fallbackProfile.hourlyRate,
            }
            : fallbackProfile;
        const key = isLabor
            ? `labor:${profile.id}`
            : item.groupKey || "other";
        if (!byGroup.has(key)) {
            const g = groupMeta.get(key);
            byGroup.set(key, {
                groupKey: key,
                label: isLabor ? profile.label : g?.label || key.replace(/-/g, " "),
                profile,
                laborHours: 0,
                purchases: 0,
                customerPurchases: 0,
                wixCost: 0,
                pieces: [],
            });
        }
        const row = byGroup.get(key);
        row.laborHours += hours;
        row.purchases += purchases;
        row.customerPurchases += customerPurchases;
        row.wixCost += wixCost;
        row.pieces.push({
            id: item.id,
            title: item.title,
            laborHours: hours,
            purchases,
            wixCost,
        });
    }

    const lines = [...byGroup.values()]
        .map((row) => {
            const rateCost = laborCostFromHours(row.laborHours, row.profile);
            return {
                ...row,
                rateCost,
                budget: rateCost + row.purchases * (1 + materialMarkupPercent / 100),
                purchasesCustomer: row.purchases * (1 + materialMarkupPercent / 100),
                roughTotal: rateCost + row.purchases * (1 + materialMarkupPercent / 100) + row.customerPurchases,
            };
        })
        .sort((a, b) => a.label.localeCompare(b.label));

    const totals = lines.reduce(
        (acc, row) => {
            acc.laborHours += row.laborHours;
            acc.rateCost += row.rateCost;
            acc.purchases += row.purchases;
            acc.customerPurchases += row.customerPurchases;
            acc.wixCost += row.wixCost;
            acc.budget += row.budget;
            acc.roughTotal += row.roughTotal;
            return acc;
        },
        { laborHours: 0, rateCost: 0, purchases: 0, customerPurchases: 0, wixCost: 0, budget: 0, roughTotal: 0 }
    );

    const quoteJob = scopeItem && scopeItem.id !== job?.id
        ? { ...job, ...scopeItem, sourceJobTitle: job?.title, selectedItemTitle: scopeItem.title }
        : job;
    const components = buildWixComponentTree(quoteRoot, items, { material_markup_percent: materialMarkupPercent }, scopeItem);
    const customerBudget = components.reduce((sum, node) => sum + (Number(node.customerBuiltUp) || 0), 0);
    return {
        job: quoteJob,
        sourceJob: job,
        selectedItem: scopeItem || job,
        lines,
        totals: { ...totals, customerBudget },
        components,
    };
}

function isComponentNode(node) {
    const kind = String(node?.lineKind || "").toLowerCase();
    return kind !== "labor" && kind !== "purchase";
}

function isCustomerPurchaseNode(node) {
    return String(node?.lineKind || "").toLowerCase() === "purchase"
        && String(node?.purchaseTreatment || "").toLowerCase() === "customer";
}

function collectCustomerPurchaseItems(nodes, out = []) {
    for (const node of nodes || []) {
        if (node.customerVisible === false) continue;
        if (isCustomerPurchaseNode(node)) {
            const title = String(node.title || "").trim();
            if (title) {
                out.push({
                    id: node.id,
                    title,
                    amount: Number(node.ownCustomerPurchases) || 0,
                    notes: String(node.purchaseNotes || "").trim(),
                });
            }
        }
        collectCustomerPurchaseItems(node.children, out);
    }
    return out;
}

function includedScopeTitles(nodes) {
    return (nodes || [])
        .filter((node) => node.customerVisible !== false && !isCustomerPurchaseNode(node))
        .map((node) => String(node.title || "").trim())
        .filter(Boolean);
}

function packageFromNode(node, { id, title, children, amount } = {}) {
    const childNodes = children ?? node?.children ?? [];
    const visibleChildren = childNodes.filter((child) => child.customerVisible !== false);
    return {
        id: id || node?.id,
        title: String(title || node?.title || "Project scope").trim() || "Project scope",
        amount: amount != null ? amount : Number(node?.customerBuiltUp) || 0,
        included: includedScopeTitles(visibleChildren),
        customerItems: collectCustomerPurchaseItems(visibleChildren),
    };
}

/**
 * Customer-facing packages: one total per system, with included work as unpriced
 * bullets. Dollars are itemized only for customer-purchased equipment.
 */
export function customerQuotePresentationFromComponents(components = []) {
    const roots = Array.isArray(components) ? components : [];
    const nestedComponents = roots
        .flatMap((root) => (root.children || []).filter(isComponentNode))
        .filter((node) => node.customerVisible !== false);
    const packageNodes = (nestedComponents.length ? nestedComponents : roots.filter(isComponentNode))
        .filter((node) => node.customerVisible !== false);
    const sharedRows = nestedComponents.length
        ? roots.flatMap((root) => (root.children || []).filter((child) => !isComponentNode(child) && child.customerVisible !== false))
        : [];

    const packages = packageNodes.map((node) => packageFromNode(node));

    if (sharedRows.length) {
        const amount = sharedRows.reduce(
            (sum, row) => sum + (Number(row.customerBuiltUp) || 0),
            0
        );
        const included = includedScopeTitles(sharedRows);
        const customerItems = collectCustomerPurchaseItems(sharedRows);
        if (amount > 0 || included.length || customerItems.length) {
            packages.push(
                packageFromNode(null, {
                    id: "parent-shared-scope",
                    title: "Project services",
                    children: sharedRows,
                    amount,
                })
            );
        }
    }

    return {
        parentTitles: nestedComponents.length
            ? roots.map((root) => String(root.title || "").trim()).filter(Boolean)
            : [],
        packages: packages.filter((pkg) => pkg.amount > 0 || pkg.included.length || pkg.customerItems.length),
        customerItems: collectCustomerPurchaseItems(roots),
    };
}

export function customerQuotePackagesFromComponents(components = []) {
    return customerQuotePresentationFromComponents(components).packages;
}

export function formatCustomerQuotePackagesText(packages = [], currency = "USD") {
    return (packages || [])
        .map((pkg) => {
            const head = `${pkg.title} — ${formatMoney(pkg.amount, currency)}`;
            const bullets = (pkg.included || []).join("\n");
            return bullets ? `${head}\n${bullets}` : head;
        })
        .join("\n\n");
}

export function estimateLinesFromWixQuote(built) {
    const packages = customerQuotePackagesFromComponents(built?.components);
    if (packages.length) {
        return packages
            .filter((pkg) => pkg.amount > 0)
            .map((pkg) => ({
                id: newEstimateLineId(),
                label: pkg.title,
                rate_key: null,
                qty: 1,
                unit: "flat",
                bill_rate: Math.round(pkg.amount * 100) / 100,
                group_id: null,
            }));
    }
    const budget = Number(built?.totals?.customerBudget) || 0;
    if (budget > 0) {
        return [{
            id: newEstimateLineId(),
            label: built?.selectedItem?.title || built?.job?.title || "Project services",
            rate_key: null,
            qty: 1,
            unit: "flat",
            bill_rate: Math.round(budget * 100) / 100,
            group_id: null,
        }];
    }
    return [];
}

function pct(part, whole) {
    if (!whole) return 0;
    return (part / whole) * 100;
}

/**
 * Internal P&L view: full cost including items blocked from the customer quote.
 */
export function internalQuoteEconomicsFromComponents(components = [], {
    customerTotal,
    actualHours = 0,
    extraMaterialCost = 0,
} = {}) {
    const acc = {
        laborHours: 0,
        laborCost: 0,
        customerLaborHours: 0,
        customerLaborCost: 0,
        internalLaborHours: 0,
        internalLaborCost: 0,
        purchaseCost: 0,
        customerPurchaseCost: 0,
        internalPurchaseCost: 0,
        customerPurchaseCharge: 0,
        customerSupplied: 0,
        hiddenCustomerPurchases: 0,
    };

    function walk(nodes) {
        for (const node of nodes || []) {
            const hidden = node.customerVisible === false;
            const hours = Number(node.ownHours) || 0;
            const labor = Number(node.ownCost) || 0;
            const purch = Number(node.ownPurchases) || 0;
            const purchCharge = Number(node.ownPurchasesCustomer) || 0;
            const custBuy = Number(node.ownCustomerPurchases) || 0;
            acc.laborHours += hours;
            acc.laborCost += labor;
            acc.purchaseCost += purch;
            if (hidden) {
                acc.internalLaborHours += hours;
                acc.internalLaborCost += labor;
                acc.internalPurchaseCost += purch;
                acc.hiddenCustomerPurchases += custBuy;
            } else {
                acc.customerLaborHours += hours;
                acc.customerLaborCost += labor;
                acc.customerPurchaseCost += purch;
                acc.customerPurchaseCharge += purchCharge;
                acc.customerSupplied += custBuy;
            }
            walk(node.children);
        }
    }
    walk(components);

    const materialMarkupAmount = acc.customerPurchaseCharge - acc.customerPurchaseCost;
    const materialMarkupPercent = acc.customerPurchaseCost > 0
        ? pct(materialMarkupAmount, acc.customerPurchaseCost)
        : 0;
    const customerCharge = acc.customerLaborCost + acc.customerPurchaseCharge;
    // Billed labor is sell price, not shop cost. Cash out is PBR purchases plus
    // internal-only labor the customer never sees.
    const extraMaterials = Number(extraMaterialCost) || 0;
    const loggedHours = Number(actualHours) || 0;
    const trueCost = acc.purchaseCost + acc.internalLaborCost + extraMaterials;
    const absorbedInternal = acc.internalLaborCost + acc.internalPurchaseCost;
    const revenue = Number(customerTotal) > 0 ? Number(customerTotal) : customerCharge;
    const grossProfit = revenue - trueCost;
    const laborProfit = acc.customerLaborCost;
    const hoursVariance = loggedHours - (acc.laborHours || 0);
    return {
        ...acc,
        materialMarkupAmount,
        materialMarkupPercent,
        customerCharge,
        trueCost,
        absorbedInternal,
        laborProfit,
        extraMaterialCost: extraMaterials,
        actualHours: loggedHours,
        hoursVariance,
        revenue,
        grossProfit,
        marginPct: pct(grossProfit, revenue),
        markupOnCostPct: pct(grossProfit, trueCost),
    };
}
